// import './wdyr'
import React, { Suspense, useRef } from 'react'
import ReactDOM from 'react-dom'
import '@fs/react-scripts/polyfills'
import { css } from '@emotion/core'
// import * as Sentry from '@sentry/browser'
import { setAppLocales } from '@fs/zion-locale'
import Root, { FrontierBaseProviders } from '@fs/zion-root'
import { AppLayout } from '@fs/zion-ui'
import { Router, useLocation } from '@fs/zion-router'
import { appPath, targetEnv } from '@fs/zion-config'
import { NoticeLoading } from '@fs/zion-icon'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { QueryClientProvider } from '@tanstack/react-query'
import whyDidYouRender from '@welldone-software/why-did-you-render'
import { BrightspotProvider } from '@fs/zion-cms'
import App from './App'
import * as serviceWorker from './serviceWorker'
import './locales'
import { AppProvider, useAppProvider } from './providers/AppProvider'
import LanguageSyncProvider from './providers/LanguageSyncProvider'
import queryClient from './queryClient'

// Enable local feature flag development using the file "dev.flags.js"
// If that file exports anything truthy it will initialize feature flags in localhost mode
// and use the flags defined in that file, instead of flags defined in split.
const mockFlags = process.env.NODE_ENV === 'development' ? import('./dev.flags').then((m) => m.default) : undefined
const mockActivities =
  process.env.NODE_ENV === 'development' ? import('./dev.activities').then((m) => m.default) : undefined

// Here we pass the mounted app path from the server to the client.
const base = appPath ? new URL(appPath).pathname : ''

const containerStyles = css`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  flex: 1;
`

const GiveRoot = (): JSX.Element => {
  const ref = useRef(null)
  const { pathname } = useLocation()
  const { shareablePage, skipLinkId, isFullscreen } = useAppProvider()
  const isRecordHintsPage = pathname.includes('record-hints')
  const isEventsPage = pathname.includes('events')
  const isQuickNameReviewPage = pathname.includes('quick-name-review')
  const isFullNameReviewPage = pathname.includes('full-name-review')
  const verifyPlaces = pathname.includes('verify-places')
  const imageViewerPage = isQuickNameReviewPage || isRecordHintsPage || isFullNameReviewPage

  const header = {
    hidden: shareablePage || isFullscreen,
  }

  const footer = {
    hidden: shareablePage || imageViewerPage || verifyPlaces || isFullscreen,
  }

  return (
    <QueryClientProvider client={queryClient}>
      <div ref={ref} css={containerStyles}>
        <Root analytics header={header} helpChatbot={false} footer={footer} skipLinkId={skipLinkId}>
          {isEventsPage ? (
            <App />
          ) : (
            <AppLayout fullWidth noScroll={isRecordHintsPage || isQuickNameReviewPage || isFullNameReviewPage}>
              <App />
            </AppLayout>
          )}
        </Root>
      </div>
      <ReactQueryDevtools />
    </QueryClientProvider>
  )
}

// add any app logic here, languages and translations are configured and available here
const FrontierWrapper = (): JSX.Element => {
  setAppLocales([
    'cs',
    'da',
    'de',
    'en',
    'es',
    'fi',
    'fr',
    'hu',
    'it',
    'ja',
    'ko',
    'no',
    'pt',
    'ro',
    'ru',
    'sm',
    'sv',
    'uk',
    'zh',
    'zh-hans',
  ]) // number of locales must match server.js

  return (
    <BrightspotProvider sites={['global-campaigns']}>
      <AppProvider>
        <Router basename={base}>
          <LanguageSyncProvider>
            <GiveRoot />
          </LanguageSyncProvider>
        </Router>
      </AppProvider>
    </BrightspotProvider>
  )
}

// Do not make changes to FrontierRoot, all changes should be made to FrontierWrapper
const FrontierRoot = (): JSX.Element => {
  return (
    <Suspense fallback={<NoticeLoading />}>
      <FrontierBaseProviders mockFlags={mockFlags} mockActivities={mockActivities}>
        <FrontierWrapper />
      </FrontierBaseProviders>
    </Suspense>
  )
}

if (process.env.NODE_ENV !== 'production') {
  whyDidYouRender(React, { include: [] })
}

ReactDOM.render(<FrontierRoot />, document.getElementById('root'))

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister()

if (targetEnv === 'local') {
  ;(async () => {
    const { default: axe } = await import('@axe-core/react')
    axe(React, ReactDOM, 1000)
  })()
}
